/**
 * InventoryWorklist — the Inventory & Ordering module's own tab.
 * Lane 500 (built) -> Lane 521 (QA round 8) -> **Lane 526 (this file)**.
 *
 * THE MOCKUP IS THE SPEC
 * ----------------------
 * Three rounds of patching the live page failed; Ryan's verdict on round 8 was
 * *"This is a mess, to be honest. I hate this."* Lane 523 then drew the page as
 * a static picture from real staging figures — `backend/scripts/inventory_page_mockup.html`
 * — and it is the first version he read successfully, unaided, out loud:
 *
 *   *"910 on hand, zero on order… if we use 58 a month, until we're 8.8 months
 *    in the max, we'll probably order again. Don't order again until May."*
 *
 * This file makes that picture real. Where this page and the mockup differ, the
 * mockup wins unless the difference is named in a comment right here.
 *
 * THE HIERARCHY IS THE FIX, NOT THE WORDING (QA round 9, item 2)
 * --------------------------------------------------------------
 * Ryan: *"It's not clear that the three below it — the 968, the 427, and the 158
 * — that's what makes that up… there's the total, there's the stuff we can't do
 * anything with, you click into that, you see the breakdown below that, you
 * click into that, you see more. Then there's the 1,723,358 that we have the
 * ability to get after."*
 *
 * He is arithmetically right and the mockup hid it: 968,059 + 427,879 + 158,703
 * = 1,554,641 exactly. The three refusal blocks ARE the untouchable line, and
 * both the old page and the mockup presented them as its siblings. Here they are
 * NESTED INSIDE it — collapsed, opening in place, indented under the figure they
 * decompose — so the relationship is structural rather than something the reader
 * has to derive by adding three numbers in their head.
 *
 * THE DEFAULT PAGE IS THE MONEY YOU CAN GET AFTER
 * -----------------------------------------------
 * Everything else is a drill-down, closed on arrival: the refusal blocks, the
 * moves set aside under the materiality floor, and the items below your stated
 * minimum. Nothing is deleted — this module's discipline is that money it
 * declines to act on is itemised, never hidden — but only one thing is open.
 *
 * DISPLAY ONLY. Every figure renders verbatim from GET /api/inventory/worklist,
 * which itself computes nothing. This page performs NO arithmetic: not a sum,
 * not a difference, not a unit conversion. Where the story wants a number the
 * server does not hold, the clause carrying it is omitted rather than derived.
 *
 * IT DOES NOT RE-SORT, AND NO COLUMN HEADER IS CLICKABLE
 * ------------------------------------------------------
 * The order arrives ranked by the calculation layer and is rendered as received.
 * Every other Linear table in this app has sortable headers; this one
 * deliberately does not, because one clickable `Until under max` header would
 * kill the ranking invariant while every backend test stayed green.
 *
 * TOLERATES FIELDS THAT DO NOT EXIST YET
 * --------------------------------------
 * Lane 525 is adding to this endpoint in parallel — new refusal groups, an
 * uncapped display projection, a re-ranked row list, the set-aside rows. Its
 * changes are additive. This page is built so that an ABSENT new field produces
 * today's behaviour: never a crash, never a blank, never an invented figure.
 * That property is what makes building the two in parallel safe, and it is
 * pinned by test (`absent fields`).
 *
 * Flag gating: mounts only when INVENTORY_ORDERING_ENABLED is ON (route in
 * App.js and nav in Sidebar.js are both gated on the /me flag). A 404 from the
 * API renders a quiet module-unavailable state, not an error.
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { API_BASE } from '../config';
import {
  ACTION_LABELS, SAFETY_LABELS, SUPPRESSION_LABELS,
  SUPPRESSION_GROUPS, SUPPRESSION_GROUP_LABELS, SUPPRESSION_GROUP_NOTES,
  labelFor, orderGroups, formatUsd, formatQty, formatQtyWhole, formatDate,
  actionParts, moveValue, rowCells, isSafetyBreach,
  reasonDefinition, groupDefinition, showsGroupNote,
  reorderPointAboveMaxNote,
  cashReleasedSplit, CASH_RELEASED_BASIS_NOTE, USAGE_BASIS_NOTE,
} from '../utils/inventoryOrderingDisplay';
import './InventoryWorklist.linear.css';

// Only the two DRAWDOWN verbs. `increase_next_order_to` left the queue for its
// own section (it is service risk, not value capture), so offering it here would
// be a dropdown that always returns nothing.
const ACTION_OPTIONS = [
  { value: '', label: 'All moves' },
  { value: 'hold_until', label: ACTION_LABELS.hold_until },
  { value: 'reduce_next_order_to', label: ACTION_LABELS.reduce_next_order_to },
];

// How many example items a refusal block shows when opened. The server already
// caps and dollar-ranks the context rows; this caps what one block renders so
// opening "Using it up will not clear this" does not unroll 1,535 rows into the
// page. The block always states the full count beside the sample.
const BLOCK_SAMPLE = 12;

/** The moves the materiality floor set aside, if the server sends them.
 *
 *  QA round 9, item 5 — Ryan on the ~$943,000 of sub-floor moves: *"that's all
 *  those tiny ones, right? Which they should still be able to see if they need
 *  it."* Today the endpoint counts them (`meta.below_floor_rows` /
 *  `below_floor_dollar`) but does not return them; Lane 525 is adding the rows.
 *  Both candidate key names are read so the drill-down lights up on arrival; if
 *  neither appears the section still renders the count and the dollar, which is
 *  what ships today. Never fabricated, never silently dropped. */
function setAsideRows(data) {
  return (data && (data.below_floor || data.set_aside)) || [];
}

/**
 * A page-local "why does this say that?" bubble — the round-10 items 2 and 4.
 *
 * WHY NOT `MetricInfo`, WHICH ALREADY EXISTS. Two reasons, the first measured:
 *
 * 1. **The 120-char cap rules it out.** MetricInfo renders the registry's
 *    `short`, capped at 120 characters by two drift gates
 *    (`metricExplanations.shape.test.js:12` and
 *    `backend/tests/test_metric_explanations.py:50`). Rendered over the 18 live
 *    rows that carry one, the reorder-date sentence is **140-148 characters —
 *    18 of 18 over the cap**. Fitting it means cutting copy Ryan approved in
 *    round 9, when he had explicitly asked for MORE than disclosure (*"fix the
 *    underlying setting, not just explain it"*). Raising the cap for one entry
 *    weakens the one-glance rule for all 15.
 * 2. **The registry is a per-METRIC dictionary.** Every entry defines what a
 *    COLUMN means. The reorder-date sentence is a per-ROW finding, present on 18
 *    of 98 rows and absent on the other 80.
 *
 * So: the same glyph, the same popover geometry, the same interaction model —
 * built here, with `metricExplanations.json` untouched and its drift gate not
 * involved. The two read as one component on the page.
 *
 * THREE THINGS INHERITED FROM MetricInfo, WHICH LEARNED THEM THE HARD WAY:
 *  - **The card is PORTALED to `document.body`.** `.invw-table` sets
 *    `display:block; overflow-x:auto; white-space:nowrap` below 1000px, and an
 *    in-place absolutely-positioned card is CLIPPED INVISIBLE by any overflow
 *    ancestor (Lane 142 caught this live). The portal also escapes that
 *    inherited `nowrap`, which is why the card sets `white-space:normal`
 *    explicitly — a sentence inheriting nowrap rendered as one 816px line here
 *    once already.
 *  - **Hover to peek, click to pin, ESC / click-outside / scroll to close**,
 *    with the ~140ms grace delay: the card is portaled, so the pointer leaves
 *    the trigger on its way into it and an immediate close makes it unreachable.
 *  - **Two-pass placement.** The card renders hidden, is measured, then
 *    positioned and revealed — so it never pokes off the top of the viewport.
 *
 * `title` carries the same sentence as a no-JS/no-pointer fallback.
 */
function InvwInfo({ heading, body, label }) {
  const [open, setOpen] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [pos, setPos] = useState(null);
  const triggerRef = useRef(null);
  const cardRef = useRef(null);
  const closeTimer = useRef(null);
  const pinnedRef = useRef(false);
  pinnedRef.current = pinned;

  const cancelClose = useCallback(() => {
    if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; }
  }, []);
  const closeSoon = useCallback(() => {
    cancelClose();
    closeTimer.current = setTimeout(() => {
      if (!pinnedRef.current) setOpen(false);
    }, 140);
  }, [cancelClose]);
  useEffect(() => cancelClose, [cancelClose]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') { setPinned(false); setOpen(false); } };
    const onDown = (e) => {
      const inTrigger = triggerRef.current && triggerRef.current.contains(e.target);
      const inCard = cardRef.current && cardRef.current.contains(e.target);
      if (!inTrigger && !inCard) { setPinned(false); setOpen(false); }
    };
    const onScroll = () => { setPinned(false); setOpen(false); };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('scroll', onScroll, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) { setPos(null); return; }
    if (!triggerRef.current || !cardRef.current) return;
    try {
      const r = triggerRef.current.getBoundingClientRect();
      const cardH = cardRef.current.getBoundingClientRect().height || 0;
      const vw = window.innerWidth || 1024;
      const vh = window.innerHeight || 768;
      const left = Math.max(8, Math.min(r.left, vw - 348));
      const above = r.top - 6 - cardH;
      let top;
      let maxHeight = null;
      if (above >= 8) top = above;
      else if (r.bottom + 6 + cardH <= vh - 8) top = r.bottom + 6;
      else { top = 8; maxHeight = vh - 16; }
      setPos({ left, top, maxHeight });
    } catch (e) {
      setPos({ left: 8, top: 8, maxHeight: null });
    }
  }, [open]);

  return (
    <>
      <button
        type="button"
        ref={triggerRef}
        className="invw-info"
        aria-label={label}
        aria-expanded={open}
        title={body}
        onMouseEnter={() => { cancelClose(); setOpen(true); }}
        onMouseLeave={closeSoon}
        onFocus={() => { cancelClose(); setOpen(true); }}
        onBlur={closeSoon}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (pinned) { setPinned(false); setOpen(false); } else { setPinned(true); setOpen(true); }
        }}
      >
        <svg width="12" height="12" viewBox="0 0 16 16" fill="none"
             aria-hidden="true" focusable="false" style={{ display: 'block' }}>
          <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" />
          <circle cx="8" cy="4.6" r="0.95" fill="currentColor" />
          <rect x="7.15" y="6.6" width="1.7" height="5" rx="0.85" fill="currentColor" />
        </svg>
      </button>
      {open && createPortal(
        <div
          ref={cardRef}
          className="invw-pop"
          role="tooltip"
          onMouseEnter={cancelClose}
          onMouseLeave={closeSoon}
          style={{
            position: 'fixed',
            zIndex: 1000,
            left: pos ? pos.left : 0,
            top: pos ? pos.top : 0,
            visibility: pos ? 'visible' : 'hidden',
            maxHeight: pos && pos.maxHeight ? pos.maxHeight : undefined,
            overflowY: pos && pos.maxHeight ? 'auto' : undefined,
            // Portaled to body, so the page's own resets do not reach it and the
            // table's `nowrap` / `th` text-transform cannot either. Explicit.
            whiteSpace: 'normal',
            textTransform: 'none',
            letterSpacing: 'normal',
            textAlign: 'left',
          }}
        >
          {heading && <div className="invw-pop-h">{heading}</div>}
          <div className="invw-pop-b">{body}</div>
        </div>,
        document.body,
      )}
    </>
  );
}

export default function InventoryWorklist() {
  const { authFetch } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unavailable, setUnavailable] = useState(false);
  const [error, setError] = useState(null);
  const [action, setAction] = useState('');
  const [site, setSite] = useState('');
  // Every drill-down starts CLOSED: the default page is the money you can act on.
  const [showCantFix, setShowCantFix] = useState(false);
  const [showSplit, setShowSplit] = useState(false);
  const [openGroups, setOpenGroups] = useState([]);
  const [showSetAside, setShowSetAside] = useState(false);
  const [showReplenishment, setShowReplenishment] = useState(false);

  const toggleGroup = useCallback((g) => {
    setOpenGroups((cur) => (cur.includes(g)
      ? cur.filter((x) => x !== g) : cur.concat(g)));
  }, []);

  // WHY THIS REF EXISTS — it is the fix for Ryan's "could not load the worklist".
  //
  // `authFetch` is a plain function in AuthContext's component body, so it has a
  // NEW IDENTITY on every AuthProvider render. Listing it in a dependency array
  // therefore re-fires whatever depends on it whenever anything in auth state
  // moves. That is normally invisible; here each re-fire is a **~7-second**
  // request that holds one of only THREE pooled connections
  // (`pool_size=2 + max_overflow=1`, `pool_timeout=5`).
  //
  // Measured, Lane 521: four of these requests SERIALLY are all 200; the same
  // four CONCURRENTLY produce a 500 at exactly 5.16s — the pool timeout — which
  // the handler converts into the error this page was rendering. It failed for
  // Ryan once and worked later because it is a race, not a filter value.
  //
  // Holding `authFetch` in a ref keeps the fetch identity stable so the effect
  // fires ONCE per mount. The filters no longer refetch at all (see below), so a
  // single user can no longer collide with themselves.
  const authFetchRef = useRef(authFetch);
  authFetchRef.current = authFetch;

  // Whether the server said it sent the WHOLE queue. Held in a ref, not state,
  // because it must not itself trigger a render/refetch cycle — that is the very
  // loop this lane is removing.
  const queueCompleteRef = useRef(true);

  const load = useCallback(async (filters) => {
    setLoading(true);
    setError(null);
    try {
      // Normally NO FILTER PARAMS: the server returns the whole floored queue and
      // filtering happens client-side, so a dropdown change costs nothing. Params
      // are sent ONLY on the fallback path below, when the server has told us the
      // queue was truncated. (The endpoint honours `?action=` / `?site=` for API
      // callers either way.)
      const qs = new URLSearchParams();
      if (filters && filters.action) qs.set('action', filters.action);
      if (filters && filters.site) qs.set('site', filters.site);
      const res = await authFetchRef.current(
        `${API_BASE}/api/inventory/worklist${qs.toString() ? `?${qs}` : ''}`);
      if (res.status === 404) { setUnavailable(true); setData(null); return; }
      if (!res.ok) throw new Error('request failed');
      const body = await res.json();
      queueCompleteRef.current = !(body && body.meta
                                   && body.meta.queue_complete === false);
      setData(body);
      setUnavailable(false);
    } catch (e) {
      setError('Could not load the worklist.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(null); }, [load]);

  // THE FALLBACK, and it is a real guard rather than a comment claiming one.
  // `MATERIALITY_FLOOR_USD` is an explicit Ryan-decides placeholder: lower it far
  // enough and the queue outgrows the server's cap, at which point filtering a
  // truncated list locally would silently hide rows that match the filter. When
  // the server says the queue is incomplete we ask IT to filter instead.
  // No-ops on the normal path (the ref starts true and the queue is 114 of a
  // 500 cap today), so this costs nothing unless the floor moves.
  useEffect(() => {
    if (queueCompleteRef.current) return;
    load({ action, site });
  }, [action, site, load]);

  // CLIENT-SIDE FILTERING, guarded on the server's own completeness claim rather
  // than on an assumption. If `queue_complete` were ever false the client is
  // holding a truncated queue and filtering it locally would silently hide rows,
  // so it falls back to showing what it has and says so.
  const allRows = useMemo(() => (data && data.rows) || [], [data]);
  const rows = useMemo(() => allRows.filter(
    (r) => (!action || r.action === action)
        && (!site || r.warehouse_code === site)), [allRows, action, site]);

  // Group the named refusals. SELECTION of server-computed dollars into
  // pre-declared buckets — no arithmetic beyond adding dollars the server
  // already itemised, which is the one sum the grouping requires. A reason this
  // file has never heard of lands in `unexplained` rather than vanishing.
  const meta = (data && data.meta) || {};
  const gap = meta.cash_released_gap || {};
  const byGroup = useMemo(() => {
    const out = {};
    Object.entries((meta.cash_released_gap || {}).suppressed_by_reason || {})
      .forEach(([reason, agg]) => {
        const g = SUPPRESSION_GROUPS[reason] || 'unexplained';
        if (!out[g]) out[g] = { cells: 0, dollars: 0, reasons: [] };
        out[g].cells += agg.cells;
        out[g].dollars += agg.over_max_dollar;
        out[g].reasons.push([reason, agg]);
      });
    return out;
  }, [meta.cash_released_gap]);

  // Context rows bucketed the same way, so opening a block shows ITS items.
  const suppressedByGroup = useMemo(() => {
    const out = {};
    ((data && data.suppressed) || []).forEach((r) => {
      const g = SUPPRESSION_GROUPS[r.suppression_reason] || 'unexplained';
      (out[g] = out[g] || []).push(r);
    });
    return out;
  }, [data]);

  if (unavailable) {
    return (
      <div className="invw">
        <div className="invw-empty">Inventory &amp; Ordering is not enabled.</div>
      </div>
    );
  }
  if (loading && !data) {
    return <div className="invw"><div className="invw-empty">Loading…</div></div>;
  }
  if (error) {
    return <div className="invw"><div className="invw-empty">{error}</div></div>;
  }
  if (!data) return null;

  const replenishment = data.replenishment || [];
  const setAside = setAsideRows(data);
  const groupKeys = orderGroups(Object.keys(byGroup));
  // Where `cash_released_total` sits, group by group — SELECTION of server
  // fields, never a sum of the row lists (which the route caps). See
  // `cashReleasedSplit`. `[]` on an older payload, and the block renders nothing.
  const split = cashReleasedSplit(meta);

  // ⚠️ THERE IS NO COVERAGE DOLLAR ON THIS PAGE, AND THAT IS A MEASURED VERDICT
  // RATHER THAN A MISSING FEATURE. Lane 529. Read this before "fixing" it.
  //
  // Lane 526 wrote a clause here — *"They cover $780,357 of that."* — reading
  // `meta.queue_dollar`, which the server has never emitted. The obvious repair
  // is to repoint it at `meta.groups.worklist.dollar`, the same quantity under
  // the name the engine actually ships. **That repair would be wrong**, and the
  // wrongness is arithmetic, not taste. Measured on real staging (2026-06-15,
  // company 91) by `backend/scripts/lane529_integration_preview.py`:
  //
  //   the clause's denominator is `cash_released_total` = $1,723,408.04
  //     -- the sum of `cash_released` over every row in the estate.
  //   `groups.worklist.dollar` = $603,421.37 -- the sum of `move_value`, which
  //     is `cash_released` on the 92 DELAY rows but `cash_avoided_now` on the
  //     6 CUT rows.
  //   so $528,551.38 of it sits inside that denominator and $74,869.99 does
  //     not sit in it AT ALL: `cash_avoided_now` is a different field the
  //     denominator never touches.
  //
  // It is not a subset, so "of that" is false. Emphatically on 546088 @W51,
  // whose `move_value` is $34,228.78 against its own `cash_released` of
  // $15,049.01 — the numerator exceeds what the denominator holds for that row.
  //
  // This is the same base-mixing that produced the bogus "$943,001" in QA round
  // 9 (an over-max total minus a move-value total), which Lane 525 called out as
  // *"the base-mixing this module forbids everywhere else"*. Shipping it here
  // would reintroduce it on the hero.
  //
  // The honest figure — the `cash_released` of just the queue's rows — IS a true
  // subset, and no surface may compute it: this page performs no arithmetic, and
  // summing 98 rows here is exactly the derivation Lane 526 refused. It belongs
  // in the calculation layer, and it is filed there. Until it exists the fourth
  // hero line states the COUNT only, and the set-aside footnote below still
  // tells the reader where the rest of the money went.
  //
  // Pinned by `does not render a coverage dollar in the hero`.

  return (
    <div className="invw">
      <header className="invw-head">
        <h1 className="invw-title">Inventory &amp; Ordering</h1>
        <p className="invw-sub">
          What to order next, and what to hold off on.
          {meta.snapshot_date && <> Stock as of {formatDate(meta.snapshot_date)}.</>}
        </p>
      </header>

      {/* THREE HORIZONTAL CARDS — Ryan, QA round 10 decision a, and the layout he
          approved in the round-10 mockup. Same figure strip every other page in
          this app uses (`.sd-figs` on the supplier dossier, `.matd-figs` on the
          material dossier), prefixed `invw-` because the page-scope-root rule
          requires it and because those two are not a shared component.

          THE THREE ARE AN EXACT IDENTITY, and that is why they can sit side by
          side: over-max ($3,277,999) = will-fix ($1,723,408) + will-not-fix
          ($1,554,591), residual 0.000000 on the live payload. The page states
          the relationship in WORDS on card A; it does not compute it.

          WHAT WAS GIVEN UP, named because Ryan approved the thing it replaces:
          round 9 nested the three refusal blocks UNDERNEATH the untouchable
          figure so the decomposition was structural rather than something the
          reader derives. Three cards across cannot hold a nested block. The
          disclosure stays inside card C and the blocks open directly beneath the
          strip, and card A's meta line now says the split in words instead. */}
      <section className="invw-hero" data-testid="invw-hero">
        <div className="invw-figs">
          <div className="invw-fig">
            <div className="k">Above your stated maximums</div>
            <div className="v">{formatUsd(meta.over_max_dollar_total)}</div>
            <div className="m">Splits into the two cards beside it</div>
          </div>

          <div className="invw-fig invw-fig-can">
            <div className="k">Ordering differently will fix</div>
            <div className="v">{formatUsd(meta.cash_released_total)}</div>
            <div className="m">
              {/* ⚠️ DELIBERATELY NOT CAUSAL. Ryan described this card as *"there's
                  $1.7 that we can go after … can be done in 98 moves"*, and
                  measured on this payload the 98 queue rows carry $563,771 of it
                  — 32.7%. Saying the 98 moves realise the $1.7M is wrong by 3x,
                  and it is the same shape as the coverage-dollar claim Lane 529
                  measured and removed (see the ⚠️ block above the return). The
                  count is stated as adjacency; the split below states the rest. */}
              {formatQty(meta.queue_rows)} moves clear the size floor and are
              {' '}ranked below
              {split.length > 0 && (
                <button type="button" className="invw-hl-more"
                        aria-expanded={showSplit}
                        onClick={() => setShowSplit((v) => !v)}>
                  {showSplit ? 'Hide where it sits' : 'See where it sits'}
                </button>
              )}
            </div>
          </div>

          <div className="invw-fig invw-fig-cant">
            <div className="k">Ordering will not fix</div>
            <div className="v invw-fig-mute">{formatUsd(gap.gap_dollar)}</div>
            <div className="m">
              {/* ⚠️ THE FOURTH CLAUSE IS A CONSEQUENCE OF THE ITEM-1 SPLIT, not an
                  independent copy change. This ENUMERATES the blocks it opens
                  into — "dead" is `disposal`, "unsafe to draw down" is `safety`,
                  "deliberately left alone" is `withheld` — so splitting
                  `no_floor` out and leaving three clauses would have left a
                  fourth block, 324 rows and $77,160, named by nothing. */}
              Dead, unsafe to draw down, deliberately left alone, or missing the
              {' '}settings we would need
              {groupKeys.length > 0 && (
                <button type="button" className="invw-hl-more"
                        aria-expanded={showCantFix}
                        onClick={() => setShowCantFix((v) => !v)}>
                  {showCantFix ? 'Hide the breakdown' : 'See what makes this up'}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* WHERE THE $1,723,408 SITS — ONE BLOCK, ONE BASIS (round 10, decision b).
            Every dollar, count and share is a server field; this page adds
            nothing. It renders EVERY group the server gave a `cash_released`,
            not a fixed three, so money can never silently fall out of the
            decomposition — see `cashReleasedSplit`. */}
        {showSplit && split.length > 0 && (
          <div className="invw-split" data-testid="invw-split">
            <p className="invw-split-note">{CASH_RELEASED_BASIS_NOTE}</p>
            <ul className="invw-split-list">
              {split.map((p) => (
                <li key={p.key} className="invw-split-row">
                  {/* The engine's own sentence for the group, one hover away.
                      This page does not restate it — a definition written here
                      is precisely what produced headings Ryan could not read. */}
                  <span className="invw-split-l" title={p.definition || undefined}>
                    {formatQty(p.rows)} {p.label}
                  </span>
                  <span className="invw-split-d">
                    {formatUsd(p.dollar)}
                    {p.sharePct !== null && p.sharePct !== undefined && (
                      <em> · {p.sharePct}%</em>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {showCantFix && groupKeys.length > 0 && (
          <div className="invw-nest" data-testid="invw-blocks">
            {groupKeys.map((g) => {
              const open = openGroups.includes(g);
              const items = suppressedByGroup[g] || [];
              return (
                <div key={g} className={`invw-block invw-block-${g}`}>
                  <button type="button" className="invw-block-h"
                          aria-expanded={open} onClick={() => toggleGroup(g)}>
                    <span className="invw-block-f">{formatUsd(byGroup[g].dollars)}</span>
                    <span className="invw-block-l">
                      {labelFor(SUPPRESSION_GROUP_LABELS, g)}
                    </span>
                    <span className="invw-block-c">
                      {formatQty(byGroup[g].cells)} items
                    </span>
                  </button>
                  {open && (
                    <div className="invw-block-body">
                      {/* The lane-written orientation note stands down as soon
                          as the block holds ONE reason whose engine sentence
                          renders below it — otherwise the two say the same
                          thing a line apart. `showsGroupNote` owns that rule so
                          all three surfaces inherit it. */}
                      {SUPPRESSION_GROUP_NOTES[g]
                        && showsGroupNote(
                          byGroup[g].reasons.map(([reason]) => reason), gap) && (
                        <p className="invw-block-note">{SUPPRESSION_GROUP_NOTES[g]}</p>
                      )}
                      <ul className="invw-block-list">
                        {byGroup[g].reasons
                          .slice()
                          .sort((a, b) => b[1].over_max_dollar - a[1].over_max_dollar)
                          .map(([reason, agg]) => (
                            <li key={reason}>
                              {/* The label/dollar pair is its OWN flex row, so
                                  the definition below is structurally a sibling
                                  of the row rather than a third item competing
                                  inside it. Making it a flex child instead let
                                  it share the line whenever the label happened
                                  to be short — measured: on the horizon reason
                                  it displaced the dollar from x=1234 to x=524
                                  while the other six wrapped correctly. */}
                              <div className="invw-block-row">
                                <span>{labelFor(SUPPRESSION_LABELS, reason)}</span>
                                <span className="invw-block-d">
                                  {formatUsd(agg.over_max_dollar)}
                                  <em> · {formatQty(agg.cells)} items</em>
                                </span>
                              </div>
                              {/* THE DEFINITION — what money is in this bucket
                                  and what the buyer would do about it. QA round
                                  9 asked for exactly this sentence, and it is
                                  the calculation layer's, rendered VERBATIM at
                                  the one grain where the keying is exact
                                  (`suppression_reason`). Never paraphrased,
                                  never substituted when absent. */}
                              {reasonDefinition(gap, reason) && (
                                <p className="invw-block-def">
                                  {reasonDefinition(gap, reason)}
                                </p>
                              )}
                            </li>
                          ))}
                      </ul>
                      {items.length > 0 && (
                        <>
                          <table className="invw-mini">
                            <thead>
                              <tr>
                                <th>Material</th><th>Whse</th>
                                <th className="invw-n">On hand</th>
                                <th className="invw-n">Max</th>
                                <th className="invw-n">Usage / mo</th>
                                <th className="invw-n">Above max</th>
                              </tr>
                            </thead>
                            <tbody>
                              {items.slice(0, BLOCK_SAMPLE).map((r) => (
                                <tr key={`${g}-${r.material_number}-${r.warehouse_code}`}>
                                  <td>
                                    <Link className="invw-mat"
                                          to={`/materials/${encodeURIComponent(r.material_number)}`}>
                                      {r.material_number}
                                    </Link>
                                    {r.material_description && (
                                      <span className="invw-desc">{r.material_description}</span>
                                    )}
                                  </td>
                                  <td className="invw-wh">W{r.warehouse_code}</td>
                                  <td className="invw-n">
                                    {formatQtyWhole((r.evidence || {}).on_hand_qty)}
                                  </td>
                                  <td className="invw-n">
                                    {formatQtyWhole((r.evidence || {}).max_qty)}
                                  </td>
                                  <td className="invw-n">
                                    {formatQtyWhole((r.evidence || {}).planning_monthly_usage)}
                                  </td>
                                  <td className="invw-n invw-money-mute">
                                    {formatUsd(r.over_max_dollar)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          <p className="invw-more">
                            Showing the {formatQty(Math.min(items.length, BLOCK_SAMPLE))}
                            {' '}biggest of {formatQty(byGroup[g].cells)}.
                          </p>
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {meta.below_floor_rows > 0 && (
          /* The floor, disclosed rather than applied silently, and REACHABLE.
             Ryan: *"that's all those tiny ones, right? Which they should still be
             able to see if they need it."*

             ⚠️ THE DOLLAR CAME OFF THIS LINE, AND IT IS A BASE DECISION, NOT A
             TRIM. It rendered `meta.below_floor_dollar` = $881,916, which is
             `groups.set_aside.dollar` on the **move_value** basis — what those
             moves are worth to MAKE. The split above now states the same 3,185
             rows as **$943,051** on the `cash_released` basis — the capital
             sitting above the stated max on them. Both are true and they are
             different quantities. Printing them a few lines apart with no basis
             label is precisely the base-mixing Ryan named in round 9 (*"the
             bogus $943,001"* — an over-max total minus a move-value total), and
             decision b is explicit that the breakdown is ONE basis. So the money
             question is answered once, above, in the same measure as the
             headline; this line keeps its job — disclose the floor, give access.
             UNSIGNED; in the lane's copy table. */
          <p className="invw-hero-floor">
            {formatQty(meta.below_floor_rows)} smaller moves set aside, each one
            worth under {formatUsd(meta.materiality_floor_usd)} to make.
            {setAside.length > 0 && (
              <button type="button" className="invw-hl-more"
                      aria-expanded={showSetAside}
                      onClick={() => setShowSetAside((v) => !v)}>
                {showSetAside ? 'Hide them' : 'See them'}
              </button>
            )}
          </p>
        )}
      </section>

      {showSetAside && setAside.length > 0 && (
        <section className="invw-aside" data-testid="invw-set-aside">
          {/* The engine's own sentence for this exact group. `data.set_aside`
              IS `by_group[GROUP_SET_ASIDE]` in the route, so the key is not a
              guess — verified at source, unlike the refusal blocks above. */}
          {groupDefinition(meta, 'set_aside') && (
            <p className="invw-block-def invw-aside-def">
              {groupDefinition(meta, 'set_aside')}
            </p>
          )}
          <QueueTable rows={setAside} caption="Moves set aside as too small" />
        </section>
      )}

      <section className="invw-filters">
        <select value={action} onChange={(e) => setAction(e.target.value)}
                aria-label="Filter by move">
          {ACTION_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <select value={site} onChange={(e) => setSite(e.target.value)}
                aria-label="Filter by warehouse">
          <option value="">All warehouses</option>
          {(meta.sites || []).map((s) => (
            <option key={s} value={s}>Warehouse {s}</option>
          ))}
        </select>
        <span className="invw-count">
          Showing {formatQty(rows.length)} of {formatQty(meta.queue_rows)}
          {meta.queue_complete === false && ' (list truncated)'}
        </span>
      </section>

      {rows.length === 0 ? (
        <div className="invw-empty">Nothing to act on with these filters.</div>
      ) : (
        <QueueTable rows={rows} testId="invw-queue" />
      )}

      {/* BELOW YOUR STATED MINIMUM — service risk, kept OUT of the queue on
          purpose. These are "order now", the opposite instruction to the queue's
          "you are above your ceiling, here is when to order next". They carry NO
          dollar (measured: 0 of 1,484 have one, because replenishing spends
          capital rather than freeing it), so the materiality floor cannot screen
          them and deliberately does not try — flooring them on a dollar they
          structurally cannot have would silently delete every stockout-risk row
          on the page. Separate, counted in full, collapsed by default. */}
      {replenishment.length > 0 && (
        <section className="invw-context">
          <button type="button" className="invw-toggle"
                  aria-expanded={showReplenishment}
                  onClick={() => setShowReplenishment((v) => !v)}>
            {showReplenishment ? 'Hide' : 'Show'} the{' '}
            {formatQty(meta.replenishment_rows)} items below your stated minimum
          </button>
          {/* The engine's own sentence, which SUPERSEDES the lane-written one.
              `data.replenishment` IS `by_group[GROUP_STOCKOUTS]` in the route,
              so this is the group's real definition and not a near-miss. The
              local sentence remains as the fallback for a payload that predates
              Lane 525 — never as a paraphrase competing with it. */}
          <p className="invw-toggle-note">
            {groupDefinition(meta, 'stockouts')
              || 'These need ordering rather than holding off, so they are not '
                 + 'ranked against the moves above and are not filtered by size.'}
          </p>
          {showReplenishment && (
            <QueueTable rows={replenishment} testId="invw-replenishment" />
          )}
        </section>
      )}
    </div>
  );
}

/**
 * The queue, as the table Ryan read.
 *
 * COLUMN ORDER IS THE STORY AND IS NOT NEGOTIABLE — QA round 9, item 6: *"DO NOT
 * redesign the row sequence."* On hand -> on order -> projected -> your max ->
 * usage -> until under max -> lead time -> what to do -> what it saves. Lead
 * time has its own column (item 3); it used to be buried in the recommendation.
 *
 * The seven data columns come from `rowCells()`, which is the SINGLE definition
 * of that sequence and of what an absent value says. The last two are rendered
 * here because they are not numbers: the instruction and the money it moves.
 *
 * NO HEADER IS CLICKABLE. See the module note.
 *
 * WHY `row.reason` IS NOT A VISIBLE COLUMN HERE, having been one on every
 * previous version of this page: the calculation layer's clause is the WHY, and
 * on a queue row the seven numeric columns now ARE the why — the mockup dropped
 * the prose and that is the version Ryan could read. It is still carried
 * verbatim as the cell's `title`, and it still renders visibly on the refusal
 * blocks' rows, where there is no numeric story to replace it. The imperative
 * cannot contradict it here: measured across all 4,644 actionable rows,
 * `hold_until` / `reduce_next_order_to` / `increase_next_order_to` have exactly
 * ONE reason shape each and ZERO contradict their verb.
 *
 * WHAT ELSE CAME OFF THE ROW, and why each is safe:
 *  - **Price signal.** The only value that changes the instruction,
 *    `improves_at_volume` ("larger orders price lower, so holding off would cost
 *    more"), is SUPPRESSED out of the queue by the calculation layer — it is a
 *    named refusal and appears in the "Held back on purpose" block above. What
 *    remains on a queue row is `holds`, `worsens_at_volume` or `unknown`, none
 *    of which alters what the buyer should do.
 *  - **Safety.** NOT dropped — it is the one that could change a decision, so it
 *    survives as a caveat chip on rows where the check did not simply pass.
 *  - **Position.** Every queue row is above max by construction; a column that
 *    reads "Above max" on all 114 rows is not information.
 */
function QueueTable({ rows, testId, caption }) {
  // Column metadata, deliberately derived from the SAME definition that renders
  // the cells — a header list written out separately is how a table starts
  // labelling one number with another number's name.
  const columns = rowCells(null);
  return (
    <table className="invw-table" data-testid={testId}>
      {caption && <caption className="invw-caption">{caption}</caption>}
      <thead>
        <tr>
          <th>Material</th>
          <th>Whse</th>
          {columns.map((c) => (
            <th key={c.key} className={c.numeric ? 'invw-n' : undefined}>
              {c.label}
              {/* WHICH RATE THIS IS (Ryan, round 10 decision d). A COLUMN
                  definition, so it belongs on the header — one bubble, not 98.
                  The per-row marker already exists and stays: the cell's
                  "last 3 months" sub-label says when the recent quarter won.
                  Measured across the 98 live rows: 53 trailing year, 45 recent
                  quarter. */}
              {c.key === 'usage' && (
                <InvwInfo
                  heading="About this rate"
                  body={USAGE_BASIS_NOTE}
                  label="Which usage rate this is"
                />
              )}
            </th>
          ))}
          <th>Recommendation</th>
          {/* ⚠️ WAS "What the move saves" (Ryan, 2026-07-30, round-9 decision 2:
              *"change the header, do not mark the rows"*).

              The column is a SELECTION between two server figures with different
              provenance, and the engine's own note on `row_move_value` forbids
              the old header outright: a `hold_until` row's figure is
              `not_reordered_proxy`, and *"any surface rendering this must not
              present it as an exact saving"*. Re-measured on the current queue by
              `backend/scripts/lane534_round9_decisions_preview.py`: **92 of 98
              rows (93.9%) are the proxy**, 6 are exact. So the old header
              overclaimed on nearly every row.

              Marking the 92 was the obvious alternative and Ryan rejected it —
              a `~` on 94% of rows stops signalling anything. Fixing the header
              is one string and costs no per-row clutter.

              WHY THIS WORDING IS TRUE ON BOTH BASES, which "saves" was not:
                cut row    `cash_avoided_now` — inbound spend not incurred.
                delay row  `cash_released`    — capital already above the stated
                                                max, which consumption unwinds.
              Both describe capital BECOMING AVAILABLE, and on each row the
              figure named is exact for the quantity it names. What the delay row
              proxies is the size of the deferred ORDER, which the feed cannot
              give us — and this header does not claim to be that. "Saves"
              claimed a realised benefit and could not be true of a position.

              UNSIGNED. See the lane's copy table. */}
          <th className="invw-n">What this frees up</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => {
          const cells = rowCells(r);
          const parts = actionParts(r);
          const money = moveValue(r);
          const policyNote = reorderPointAboveMaxNote(r);
          return (
            <tr key={`${r.material_number}-${r.warehouse_code}`}>
              <td className="invw-c-mat">
                <Link className="invw-mat"
                      to={`/materials/${encodeURIComponent(r.material_number)}`}>
                  {r.material_number}
                </Link>
                {r.material_description && (
                  <span className="invw-desc">{r.material_description}</span>
                )}
              </td>
              <td className="invw-wh">W{r.warehouse_code}</td>

              {cells.map((c) => (
                <td key={c.key}
                    className={`${c.numeric ? 'invw-n' : ''}${c.strong ? ' invw-n-strong' : ''}`}>
                  {c.value !== null && c.value !== undefined ? (
                    <>
                      {c.value}
                      {c.sub && <span className="invw-cellsub">{c.sub}</span>}
                    </>
                  ) : (
                    /* An honest absence, visibly not a number and never
                       mistakable for one. It states the CAUSE, so the reader is
                       never left to assume the missing beat was fine. */
                    <span className="invw-none">{c.absentNote || 'not available'}</span>
                  )}
                </td>
              ))}

              <td className={`invw-c-rec invw-rec-${r.action}`} title={r.reason || undefined}>
                {parts ? (
                  <>
                    {parts.lead}
                    {parts.strong && <b>{parts.strong}</b>}
                    {/* THE POLICY FINDING, now a bubble BESIDE THE DATE rather
                        than a sentence under the row (Ryan, round 10 decision c:
                        *"when you hover over 'don't reorder until June 18 2027'
                        … just have, like, an information bubble next to it"*).
                        It sits immediately after the bolded date because that is
                        the number it is about. Selection only — the sentence,
                        its guards and every figure in it still come from the
                        shared display module, unchanged. */}
                    {policyNote && (
                      <InvwInfo
                        heading="About this reorder date"
                        body={policyNote}
                        label="Why this date is before the max date"
                      />
                    )}
                    {parts.tail}
                  </>
                ) : (
                  /* No imperative is authored for a row whose verb is ambiguous
                     — `action` carries ten meanings on `no_action` alone. Those
                     rows show the calc layer's own clause instead. */
                  <span className="invw-why">{r.reason}</span>
                )}
                {/* The safety caveat, and ONLY when there is one. `unverified` is
                    55,606 of 64,867 rows estate-wide, so it must read as
                    provenance — a dimension we could not test — and never as a
                    breach we found. Membership, never `!== 'passes'`. */}
                {r.safety_check && r.safety_check !== 'passes' && (
                  <span className={`invw-flag${isSafetyBreach(r) ? ' invw-flag-bad' : ''}`}>
                    {labelFor(SAFETY_LABELS, r.safety_check)}
                  </span>
                )}
                {/* ⚠️ THE POLICY SENTENCE NO LONGER RENDERS AS A ROW NOTE — it
                    moved into the info bubble beside the date above (round 10,
                    decision c). Ryan on the round-9 version: it made the row
                    heavy, and the finding is about the DATE, so it belongs next
                    to the date. `reorderPointAboveMaxNote` and its 15 unit tests
                    are untouched; only where its output renders changed.

                    The `.invw-policy` class went with it, and so did the
                    `@media (max-width:1000px)` wrap rule that existed because a
                    sentence inheriting the table's `nowrap` rendered as one
                    816px line. That is exactly why the popover sets
                    `white-space:normal` explicitly. */}
              </td>

              {/* ONE dollar, and it is the money THIS instruction moves. Ryan:
                  *"just have it just be the number."* — the explanatory
                  sub-label is gone; the column header names it once. */}
              {/* `data-move-basis` carries the calc layer's provenance for this
                  figure — `not_received` is exact, `not_reordered_proxy` is a
                  stand-in. Nothing renders off it YET; see the note on
                  `moveValue` and handover §4.3. It is here so the treatment has
                  a hook and a test can see the basis reach the surface. */}
              <td className="invw-n invw-c-money" data-move-basis={money.basis || undefined}>
                {money.value !== null && money.value !== undefined
                  ? <span className="invw-money">{formatUsd(money.value)}</span>
                  : <span className="invw-none">Not available</span>}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
